/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Component, inject, NgZone, OnDestroy, OnInit, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DataService } from '../services/data.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ChargeToMyRoomComponent, ChargeToMyRoomResult } from './charge-to-my-room/charge-to-my-room.component';
import { Location } from '@angular/common';
import { OnlinePaymentService } from '../services/online-payment.service';
import { PaymentEventsService } from '../services/payment-events.service';
import { ApplePayService } from '../services/apple-pay.service';
import { OrderService } from '../services/order.service';
import { Router } from '@angular/router';
import { ConstantsService } from '../services/constants.service';
import { TapRedirectPaymentsService } from '../services/tap-knet-payments.service';
import { GooglePayService } from "../services/google-pay.service";
import { ApiService } from "../services/api.service";
import { OrderStatus, PaymentMode, VoucherMode, NotificationType, PaymentType } from "../shared/enums";
import { Subscription, take } from "rxjs";
import { Fee, GetOrder, PaymentMethod } from "../models/cart.model";
import { AppliedVoucher, VoucherPayload } from "../models/voucher.model";
import { NotificationPayload } from "../models/notification.model";
import { SocketEventType } from "../models/websocket.model";
import { TableSocketService } from "../services/table-socket.service";
import { ToastService } from "../services/toast.service";

@Component({
  selector: "app-bill",
  templateUrl: "./bill.component.html",
  styleUrls: ["./bill.component.scss"],
  standalone: false,
})
export class BillComponent implements OnInit, OnDestroy {
  public paymentType: PaymentMethod | undefined;
  public paymentMethods: PaymentMethod[] = [];
  public orders: GetOrder[];
  public hasDiscount: boolean;
  public submitted = false;
  public paymentMode: PaymentMode;
  public PaymentMode = PaymentMode;
  public showPayButton = true;
  public isLoading = true;
  public appliedVoucher: AppliedVoucher | null = null;
  public isOrderFree = false;
  public VoucherMode = VoucherMode;

  public fees: Fee[];
  public subtotal: number;
  public finalTotal: number;
  public discountAmount: number;
  public feesAmount: number;

  public paidSubtotal: number;
  public paidFinalTotal: number;
  public paidDiscountAmount: number;
  public paidFeesAmount: number;
  public paidFees: Fee[];
  public shouldShowBill: boolean;
  public activeView: 'current' | 'previous' = 'current';
  private hasSetInitialView = false;
  private constants = inject(ConstantsService);

  // Orders to be counted in the total payment calculation
  public billedOrders: GetOrder[];
  public paidOrders: GetOrder[];
  public voucher = signal<string | null>(null);
  public hideVoucher = signal(false);
  private transactionId: string | undefined;
  private isSettlingFreeBill = false;
  private hasFees = false;
  private userId: string;
  private restaurantId: string;
  private orderStatusSubscription: Subscription;
  constructor(
    private toastService: ToastService,
    private dataService: DataService,
    private translateService: TranslateService,
    private apiService: ApiService,
    private bottomSheet: MatBottomSheet,
    private location: Location,
    private onlinePaymentService: OnlinePaymentService,
    private paymentEventsService: PaymentEventsService,
    private applePayService: ApplePayService,
    private googlePayService: GooglePayService,
    private orderService: OrderService,
    private ngZone: NgZone,
    private router: Router,
    private tapRedirect: TapRedirectPaymentsService,
    private tableSocketService: TableSocketService
  ) { }

  public ngOnInit(): void {
    this.checkTableId();
    this.hasDiscount = false;
    this.userId = this.dataService.getUserId();
    this.restaurantId = this.dataService.restaurant()?.id;
    this.paymentMode = this.dataService.restaurant()?.payment_mode;

    this.dataService.hasFees$.subscribe((hasFees) => {
      this.hasFees = hasFees;
    });

    if (!this.userId || !this.restaurantId) {
      this.router.navigate(["/menu"]);
      return;
    } else {
      this.loadOrders();
      this.setupPaymentMethods();

      this.paymentEventsService.paymentCancelled.subscribe((cancelled) => {
        if (cancelled) {
          // angular can't detect changes made by async callbacks of third-party libraries because "they are not in angular's zone".
          // which is in our case the checkout lightbox https://angular.io/guide/zone
          this.ngZone.run(() => {
            this.isLoading = false;
            this.showPayButton = true;
          });
        }
      });

      // Keeps "My orders" current while it's open — e.g. staff cancels/voids an
      // order, or a payment confirms — without the guest having to manually refresh.
      this.orderStatusSubscription = this.tableSocketService.message$.subscribe((event) => {
        // Skipped while a free bill is settling: our own confirm triggers these
        // events, and reloading would drop the loader and flash an empty bill
        // just before the redirect to /bill-paid.
        if (
          !this.isSettlingFreeBill &&
          (event.type === SocketEventType.OrderStatus || event.type === SocketEventType.PaymentConfirmed) &&
          event.user_id === this.userId
        ) {
          this.loadOrders();
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.orderStatusSubscription?.unsubscribe();
  }

  public get showAskBill(): boolean {
    return this.showPayButton
      && this.paymentMode !== PaymentMode.Prepaid
      && !this.isOrderFree
      && this.orders?.length > 0
      && this.finalTotal > 0;
  }

  public setActiveView(view: 'current' | 'previous'): void {
    this.activeView = view;
  }

  private syncActiveView(): void {
    if (this.hasSetInitialView) return;
    this.hasSetInitialView = true;
    this.activeView = this.billedOrders?.length > 0 ? 'current' : 'previous';
  }

  private loadOrders(): void {
    this.apiService.getOrders(this.userId, this.restaurantId).subscribe((orders) => {
      // type != pre
      if (this.paymentMode !== PaymentMode.Prepaid) {
        this.orders = orders.orders;

        // Piad Orders info (previous orders)
        this.paidOrders = orders.orders.filter(
          (order) => order.status === OrderStatus.Paid
        );
        this.paidSubtotal = this.paidOrders.reduce(
          (accumulator, order) =>
            accumulator + order.total_price_without_discount,
          0
        );
        this.paidDiscountAmount = this.paidOrders.reduce(
          (accumulator, order) => accumulator + order.discount_amount,
          0
        );
        this.paidFeesAmount = this.paidOrders.reduce(
          (accumulator, order) => accumulator + order.fee_amount,
          0
        );
        this.paidFinalTotal = this.paidOrders.reduce(
          (accumulator, order) => accumulator + order.total_price,
          0
        );
        const paidFees = this.paidOrders.find(
          (order) => order.fees && order.fees?.length > 0
        )?.fees;
        this.paidFees = paidFees || [];

        // Current orders
        this.billedOrders = orders.orders.filter(
          (order) => order.status === OrderStatus.Unpaid
        );
        this.syncActiveView();

        if (this.billedOrders.length > 0) {
          // isLoading is cleared once applyPostpaidCheckout resolves the final totals.
          this.applyPostpaidCheckout(this.voucher());
        } else {
          this.isLoading = false;
        }

        // Prepaid:
      } else {
        this.orders = orders.orders.filter(
          (order) => order.status === OrderStatus.Paid
        );
        this.paidOrders = this.orders;
        this.paidSubtotal = this.orders.reduce(
          (accumulator, order) =>
            accumulator + order.total_price_without_discount,
          0
        );
        this.paidDiscountAmount = this.orders.reduce(
          (accumulator, order) => accumulator + order.discount_amount,
          0
        );
        this.paidFeesAmount = this.orders.reduce(
          (accumulator, order) => accumulator + order.fee_amount,
          0
        );
        this.paidFinalTotal = this.orders.reduce(
          (accumulator, order) => accumulator + order.total_price,
          0
        );
        this.paidFees = this.orders[0]?.fees || [];
        this.syncActiveView();
        this.isLoading = false;
      }
    });
  }

  private async setupPaymentMethods(): Promise<void> {
    const paymentMethods = this.dataService.paymentMethods();
    if (!(paymentMethods?.length > 0)) {
      this.showPayButton = false;
      return;
    }

    // Wallets are only offered when this device/browser supports them. Google Pay is
    // resolved before assigning so the options sheet never renders a slot for it
    // that then disappears.
    const applePayAvailable = this.applePayService.isApplePayAvailable();
    const googlePayAvailable = await this.googlePayService.isGooglePayAvailable().catch(() => false);

    this.paymentMethods = paymentMethods
      .filter((type) => applePayAvailable || type.key !== "applepay")
      .filter((type) => googlePayAvailable || type.key !== "googlepay");
  }

  private checkTableId(): void {
    const isJRCC = this.isJRCCMenu();
    // Hide the bill for JRCC table
    this.shouldShowBill = !isJRCC;
    if (isJRCC) {
      document.body.style.setProperty("background", "none", "important");
      document.documentElement.style.setProperty(
        "background",
        "none",
        "important"
      );
    }
  }

  private isJRCCMenu(): boolean {
    const tableId = this.dataService.table()?.id;
    return this.constants.JRCC_TABLE_IDS.includes(tableId);
  }

  public submit(): void {
    if (!this.voucher() && !this.hasFees) {
      this.confirm();
      return;
    }

    // A free bill has no gateway/sheet of its own to show progress, so cover the
    // confirm request too, not just the transaction fetch after it.
    if (this.isOrderFree) {
      this.isLoading = true;
      this.isSettlingFreeBill = true;
    }

    const restaurantId: string = this.dataService.restaurant().id;
    this.apiService
      .confirmPostpaidCheckout(restaurantId, this.getVoucherPayload(this.voucher()))
      .subscribe({
        next: (res) => {
          this.transactionId = res.transaction_id;
          this.confirm();
        },
        error: () => {
          this.isLoading = false;
          this.isSettlingFreeBill = false;
          this.toastService.error(this.translateService.instant('PAYMENT.ERROR'));
        },
      });
  }

  public async confirm(): Promise<void> {
    if (this.paymentType?.key === PaymentType.Room) {
      this.openChargeToMyRoom();
      return;
    }
    if (!this.paymentType?.is_online && this.paymentType?.key !== "applepay" && this.paymentType?.key !== "googlepay") {
      this.sendNotification();
    }

    if (
      this.paymentType?.is_online &&
      this.paymentType?.key !== "applepay" &&
      this.paymentType?.key !== "googlepay" &&
      !this.isOrderFree
    ) {
      if (this.paymentType && ['knet', 'tap'].includes(this.paymentType.key) && !this.isOrderFree) {
        this.isLoading = true;
        this.showPayButton = false;

        const orderIds = this.billedOrders.map(o => o.order_id);
        const source = this.paymentType.key === 'knet' ? 'knet' : 'all';
        this.tapRedirect.startRedirect(orderIds, source);
        return;
      }

      if (this.paymentType?.is_online && this.paymentType?.key !== 'applepay' && !this.isOrderFree) {
        this.isLoading = true;
        this.showPayButton = false;
        const orderIds = this.billedOrders.map((order) => order.order_id);
        await this.onlinePaymentService.startPayment(orderIds);
      }
    }
  }

  // Each payment method is its own button (same as the cart page), so tapping one
  // both picks the method and starts paying with it.
  public onPaymentSelected(method: PaymentMethod): void {
    this.paymentType = method;

    if (method.key === "applepay") {
      this.submitApplePay();
    } else if (method.key === "googlepay") {
      this.submitGooglePay();
    } else {
      this.submit();
    }
  }

  public submitApplePay(): void {
    if (!this.applePayService.isApplePayAvailable()) {
      const messageKey = this.applePayService.isInAppBrowser()
        ? 'BILL.APPLEPAY_UNAVAILABLE_INAPP'
        : 'BILL.APPLEPAY_UNAVAILABLE';
      const fallback = this.applePayService.isInAppBrowser()
        ? 'Apple Pay isn\'t available in this in-app browser. Open this page in Safari to use Apple Pay.'
        : 'Apple Pay is not available on this device/browser.';
      this.toastService.warning(this.translateService.instant(messageKey) || fallback);
      return;
    }

    this.submit();

    const session = this.applePayService.createApplePaySession(this.dataService.restaurant().currency, this.finalTotal);

    const orderIds = this.billedOrders.map((o) => o.order_id);
    this.applePayService.startApplePaySession(session, orderIds);

    // avoid duplicate subscriptions on repeated clicks
    const sub = this.applePayService.ApplePaySuccess.subscribe((ok) => {
      if (ok) {
        const txId = this.applePayService.getTransactionId();
        if (txId) this.orderService.setLastTransaction(txId);
      }
      sub.unsubscribe();
    });
  }

  public async submitGooglePay(): Promise<void> {
    // Debounce: ignore if already processing
    if (this.isLoading) return;
    if (!this.billedOrders?.length) return;

    this.submit();

    const orderIds = this.billedOrders.map(o => o.order_id);
    const currency = this.dataService.restaurant().currency ?? 'AED';

    const total = Number(this.finalTotal ?? 0);
    if (total <= 0) {
      this.toastService.info('Nothing to charge.');
      return;
    }
    const totalStr = total.toFixed(2);

    this.isLoading = true;
    this.showPayButton = false;

    // Subscribe ONCE per click
    this.googlePayService.GooglePaySuccess.pipe(take(1)).subscribe((ok: boolean) => {
      this.isLoading = false;
      if (ok) {
        const txId = this.googlePayService.getTransactionId() || undefined;
        if (txId) {
          this.orderService.setLastTransaction(txId).subscribe({
            next: () => this.router.navigate(['/bill-paid']),
            error: () => this.toastService.error(this.translateService.instant('PAYMENT.ERROR'))
          });
        }
      } else {
        this.showPayButton = true;
        this.toastService.error('Google Pay was cancelled or failed. Please try again or choose another method.');
      }
    });

    try {
      // Await so early errors (e.g., pay.js not loaded) hit the catch below
      await this.googlePayService.startGooglePaySession(orderIds, totalStr, currency);
    } catch (e) {
      this.isLoading = false;
      this.showPayButton = true;
      this.toastService.warning('Google Pay is unavailable on this device/browser.');
    }
  }

  public onVoucherApplied(data: { voucher: string | null }): void {
    this.applyPostpaidCheckout(data.voucher);
  }

  private openChargeToMyRoom(): void {
    const sheetRef = this.bottomSheet.open(ChargeToMyRoomComponent, { autoFocus: false });
    sheetRef.afterDismissed().subscribe((roomDetails?: ChargeToMyRoomResult) => {
      // Backdrop tap / swipe / close icon dismiss with no data — only a real submit
      // should send the "bring the bill" notification.
      if (roomDetails) this.sendNotification(roomDetails);
    });
  }
  private makeBillNotification(roomDetails?: any): NotificationPayload {
    const payload: NotificationPayload = {
      type: NotificationType.BringTheBill,
      payment_type: this.paymentType?.key as PaymentType,
      table: this.dataService.table().id,
      user: this.dataService.getUserId(),
      has_discount: this.hasDiscount,
      voucher_name: this.voucher() ?? undefined,
    };

    if (roomDetails) {
      payload.name = roomDetails.name;
      payload.room_number = roomDetails.roomNumber;
    }

    return payload;
  }

  private sendNotification(roomDetails?: any): void {
    this.submitted = true;

    // Nothing to collect, so the bill is settled here — straight to the paid
    // confirmation, same as the PayAtTable free-order flow.
    if (this.isOrderFree) {
      this.isLoading = true;
      if (!this.transactionId) {
        this.router.navigate(['/bill-paid']);
        return;
      }
      this.orderService.setLastTransaction(this.transactionId).subscribe({
        next: () => this.router.navigate(['/bill-paid']),
        error: () => this.router.navigate(['/bill-paid']),
      });
      return;
    }

    const notification: NotificationPayload =
      this.makeBillNotification(roomDetails);
    // abort if no payment type available.
    if (!notification.payment_type) {
      return;
    }
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    this.apiService.sendNotification(notification).subscribe((ok) => {
      this.toastService.success(this.translateService.instant("BILL.SNACKBAR_TITLE"));
    });
    this.location.back();
  }

  private applyPostpaidCheckout(voucherName: string | null = null): void {
    this.isLoading = true;
    const restaurantId: string = this.dataService.restaurant()?.id;
    this.apiService
      .applyPostpaidCheckout(restaurantId, this.getVoucherPayload(voucherName))
      .subscribe({
        next: (res) => {
          const voucher = Object.keys(res.voucher ?? {}).length > 0 ? res.voucher : null;
          this.voucher.set(voucherName);
          this.hideVoucher.set(voucher != null);
          this.appliedVoucher = voucher;
          this.fees = res.orders[0].fees || [];

          this.subtotal = res.orders.reduce(
            (accumulator, order) =>
              accumulator + order.total_price_without_discount,
            0
          );
          this.finalTotal = res.orders.reduce(
            (accumulator, order) => accumulator + order.total_price,
            0
          );
          this.discountAmount = res.orders.reduce(
            (accumulator, order) => accumulator + order.discount_amount,
            0
          );
          this.feesAmount = res.orders[0].fee_amount;

          if (
            this.appliedVoucher?.mode === VoucherMode.Balance &&
            this.finalTotal <= 0
          ) {
            this.dataService.voucherName.set(this.voucher());
          }

          if (this.finalTotal <= 0) {
            this.isOrderFree = true;
            this.paymentType = undefined;
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.voucher.set(null);
          this.hideVoucher.set(false);
          this.dataService.voucherName.set(null);
          this.toastService.error(this.translateService.instant(error.error.message));
          if (voucherName) {
            this.applyPostpaidCheckout();
          } else {
            this.isLoading = false;
          }
        },
      });
  }

  private getVoucherPayload(voucherName: string | null): VoucherPayload {
    return {
      table_id: this.dataService.table().id,
      voucher_name: voucherName ?? undefined,
      user_id: this.dataService.getUserId(),
      order_ids: this.billedOrders.map((order) => order.order_id),
      payment_type: this.paymentType?.key,
    };
  }
}
